import { zodResolver } from '@hookform/resolvers/zod';
import { ROLE_LABELS, setPinSchema, type SetPinInput } from '@bifeai/shared';
import { Badge, Button, Callout, Card, CardBody, CardHeader, Field, Input, PageHeader, useToast } from '@bifeai/ui';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { ChangePasswordForm } from '../components/ChangePasswordForm';
import { api, applyFieldErrors, errorMessage } from '../lib/api';
import { useSession } from '../lib/session';

export function AccountPage() {
  const { me, refresh } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const branch = me.branches.find((b) => b.id === me.membership.branchId);

  return (
    <>
      <PageHeader title="Mi cuenta" subtitle="Tus datos de acceso y la seguridad de tu usuario." />
      <div
        style={{
          display: 'grid',
          gap: 20,
          gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          alignItems: 'start',
        }}
      >
        <Card>
          <CardHeader title="Tus datos" />
          <CardBody>
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: '10px 20px',
                margin: 0,
              }}
            >
              <dt className="bf-cell-sub">Nombre</dt>
              <dd style={{ margin: 0, fontWeight: 600 }}>{me.user.name}</dd>
              <dt className="bf-cell-sub">Email</dt>
              <dd style={{ margin: 0 }}>{me.user.email}</dd>
              <dt className="bf-cell-sub">Rol</dt>
              <dd style={{ margin: 0 }}>{ROLE_LABELS[me.membership.role]}</dd>
              <dt className="bf-cell-sub">Sucursal</dt>
              <dd style={{ margin: 0 }}>{branch?.name ?? 'Todas las sucursales'}</dd>
              <dt className="bf-cell-sub">Verificación en dos pasos</dt>
              <dd style={{ margin: 0 }}>
                {me.user.totpEnabled ? (
                  <Badge tone="success" dot>
                    Activa
                  </Badge>
                ) : (
                  <Badge dot>Sin activar</Badge>
                )}
              </dd>
            </dl>
            {!me.user.totpEnabled && (
              <div style={{ marginTop: 18 }}>
                <Button variant="primary" onClick={() => navigate('/activar-2fa')}>
                  Activar verificación en dos pasos
                </Button>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Contraseña" subtitle="Al cambiarla se cierran tus sesiones en otros dispositivos." />
          <CardBody>
            <ChangePasswordForm onDone={() => toast.success('Contraseña actualizada')} />
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="PIN de mostrador" subtitle="Para cambiar de usuario rápido en la computadora de la caja, sin escribir la contraseña." />
          <CardBody>
            <PinForm
              pinSet={me.user.pinSet}
              onDone={async () => {
                toast.success(me.user.pinSet ? 'PIN actualizado' : 'PIN creado');
                await refresh();
              }}
            />
          </CardBody>
        </Card>
      </div>
    </>
  );
}

function PinForm({ pinSet, onDone }: { pinSet: boolean; onDone: () => Promise<void> }) {
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SetPinInput>({ resolver: zodResolver(setPinSchema) });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await api.post('/auth/pin', values);
      reset();
      await onDone();
    } catch (err) {
      if (!applyFieldErrors(err, setError)) setFormError(errorMessage(err));
    }
  });

  return (
    <form className="bf-form" onSubmit={onSubmit} noValidate>
      {formError && <Callout tone="danger">{formError}</Callout>}
      {pinSet && <Callout>Ya tenés un PIN. Si cargás uno nuevo, reemplaza al anterior.</Callout>}
      <Field label="PIN (4 a 6 números)" error={errors.pin?.message}>
        <Input type="password" inputMode="numeric" autoComplete="off" maxLength={6} {...register('pin')} />
      </Field>
      <Field label="Tu contraseña" hint="Para confirmar que sos vos." error={errors.currentPassword?.message}>
        <Input type="password" autoComplete="current-password" {...register('currentPassword')} />
      </Field>
      <Button type="submit" variant="primary" loading={isSubmitting}>
        {pinSet ? 'Cambiar PIN' : 'Crear PIN'}
      </Button>
    </form>
  );
}
